import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getBlogPosts } from '../services/api';
import { Calendar, ArrowRight } from 'lucide-react';
import SEO from './SEO';
import { imgErrorFallback } from '../utils/imageFallback';

const SITE_URL = 'https://camption.com';

const BlogListPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getBlogPosts(50)
      .then((r) => setPosts(r.data || []))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, []);

  const fmt = (d) =>
    d ? new Date(d).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' }) : '';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Camption Blog',
    url: `${SITE_URL}/blog`,
    description: 'Likya Yolu, Karya Yolu ve Türkiye trekking rotaları üzerine rehberler, keşif yazıları ve ekipman önerileri.',
    inLanguage: 'tr-TR',
    isPartOf: { '@type': 'WebSite', name: 'Camption', url: SITE_URL },
  };

  return (
    <section className="bg-white min-h-screen" data-testid="blog-list-page">
      <SEO
        title="Blog — Trekking Rehberleri ve Rota Hikayeleri"
        description="Likya Yolu, Karya Yolu ve Türkiye trekking rotaları üzerine kokartlı rehberler tarafından yazılmış rehberler, keşif yazıları, ekipman ve hazırlık önerileri."
        url={`${SITE_URL}/blog`}
        type="website"
        jsonLd={jsonLd}
      />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
        <nav aria-label="Breadcrumb" className="text-xs text-gray-500 mb-4">
          <ol className="flex flex-wrap items-center gap-1">
            <li><Link to="/" className="hover:text-[#3D4A26]">Ana Sayfa</Link></li>
            <li aria-hidden="true">/</li>
            <li className="text-gray-700">Blog</li>
          </ol>
        </nav>

        <h1
          className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#1B1B1B] leading-tight mb-4"
          style={{ fontFamily: 'DM Sans, sans-serif' }}
        >
          Rehberler & Keşifler
        </h1>
        <p className="text-lg text-gray-600 max-w-2xl mb-12">
          Sahadan gelen tavsiyeler, güncel rota bilgileri, hazırlık kılavuzları. Kokartlı rehberlerimizin kaleminden.
        </p>

        {loading ? (
          <p className="text-gray-500">Yükleniyor...</p>
        ) : posts.length === 0 ? (
          <p className="text-gray-500">Henüz yayınlanmış yazı yok.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((p) => (
              <Link
                key={p.id}
                to={`/blog/${p.slug}`}
                data-testid={`blog-card-${p.slug}`}
                className="group block"
              >
                <div className="aspect-[4/3] rounded-xl overflow-hidden bg-gray-100 mb-4">
                  <img
                    src={p.image}
                    alt={p.imageAlt || p.title}
                    onError={imgErrorFallback}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                {p.category && (
                  <span className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[#3D4A26] block mb-2">
                    {p.category}
                  </span>
                )}
                <h2 className="text-xl font-bold text-[#1B1B1B] leading-snug mb-2 group-hover:text-[#3D4A26]">
                  {p.title}
                </h2>
                {p.excerpt && <p className="text-sm text-gray-600 line-clamp-3 mb-3">{p.excerpt}</p>}
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span className="flex items-center gap-1"><Calendar size={12} /> {fmt(p.publishedAt || p.created_at)}</span>
                  <span className="flex items-center gap-1 text-[#3D4A26] font-semibold">Oku <ArrowRight size={12} /></span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default BlogListPage;
